import React from 'react';
import { localizedPath } from '@/lib/localizedPath';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { SupportCreator } from './SupportCreator';

/**
 * Global Footer Component
 * Appears at the bottom of every route. Includes:
 * - Internal navigation links
 * - Variant game hub links
 * - SupportCreator tip jar
 * - Fan project disclaimer (IP & brand safety)
 * - Copyright notice
 */

export function Footer() {
  const { t, i18n } = useTranslation();
  const [location] = useLocation();
  const isEnglish = (i18n.resolvedLanguage || i18n.language).split('-')[0] === 'en';
  const showUnblockedGuide = isEnglish && location === '/';

  return (
    <footer
      className="w-full border-t border-gray-200 bg-white py-8 mt-auto"
      style={{ fontFamily: 'Roboto, sans-serif' }}
    >
      <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-6">

        {/* Main Navigation Links */}
        <nav className="flex flex-wrap justify-center gap-4 text-sm">
          <a href={localizedPath('/')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.play')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/tips')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.tips')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/how-to-play')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.how_to_play')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/history')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.history')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/blog')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.blog')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/faq')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.faq')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/leaderboard')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.leaderboard')}</a>
          <span className="text-gray-300">•</span>
          <a href={localizedPath('/about')} className="text-gray-600 hover:text-gray-800 font-medium">{t('nav.about', 'About')}</a>
          {showUnblockedGuide && (
            <>
              <span className="text-gray-300">•</span>
              <a href="/unblocked" className="text-gray-600 hover:text-gray-800 font-medium">Unblocked browser guide</a>
            </>
          )}
        </nav>

        {/* Feature Links */}
        <nav className="flex flex-wrap justify-center gap-4 text-sm">
          <a href={localizedPath('/daily-challenge')} className="text-blue-600 hover:text-blue-800 font-medium">{t('nav.daily_challenge')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/hall-of-legends')} className="text-blue-600 hover:text-blue-800 font-medium">🏆 {t('nav.hall_of_legends')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/trophy-case')} className="text-blue-600 hover:text-blue-800 font-medium">🏅 {t('nav.trophy_case')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/museum')} className="text-blue-600 hover:text-blue-800 font-medium">🏛️ {t('nav.museum')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/shop')} className="text-blue-600 hover:text-blue-800 font-medium">{t('nav.shop')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/achievements')} className="text-blue-600 hover:text-blue-800 font-medium">{t('nav.achievements')}</a>
          <span className="text-gray-300">·</span>
          <a href={localizedPath('/replays')} className="text-blue-600 hover:text-blue-800 font-medium">{t('nav.replays')}</a>
        </nav>

        {/* Variant Game Hubs */}
        <nav className="flex flex-wrap justify-center gap-3 text-xs text-gray-400">
          <a href={localizedPath('/variants/fast')} className="hover:text-gray-600 transition-colors">{t('variants.fast')}</a>
          <span className="text-gray-200">·</span>
          <a href={localizedPath('/variants/night')} className="hover:text-gray-600 transition-colors">{t('variants.night')}</a>
          <span className="text-gray-200">·</span>
          <a href={localizedPath('/variants/hard')} className="hover:text-gray-600 transition-colors">{t('variants.hard')}</a>
          <span className="text-gray-200">·</span>
          <a href="https://playgianasisters.com" target="_blank" rel="noopener" className="hover:text-gray-600 transition-colors">Play Giana Sisters</a>
          <span className="text-gray-200">·</span>
          <a href="https://c64vault.com" target="_blank" rel="noopener" className="hover:text-gray-600 transition-colors">C64 Vault</a>
          <span className="text-gray-200">·</span>
          <a href="https://c64.wiki" target="_blank" rel="noopener" className="hover:text-gray-600 transition-colors">C64 Wiki</a>
          <span className="text-gray-200">·</span>
          <a href="https://pipeandbrick.com" target="_blank" rel="noopener" className="hover:text-gray-600 transition-colors">Pipe &amp; Brick</a>
        </nav>

        {/* Support Section */}
        <SupportCreator />

        {/* Fan Project Disclaimer */}
        <p className="text-xs text-gray-400 text-center max-w-lg leading-relaxed">
          {t('footer.not_affiliated')}
        </p>

        <p className="text-xs text-gray-300">
          &copy; {new Date().getFullYear()} Chrome Dino Game
        </p>

      </div>
    </footer>
  );
}
